import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiRequestError } from '../../../api/client'
import {
  useAdvanceOnboardingTarget,
  useCancelOnboardingRun,
  useOnboardingEmailPreview,
  useOnboardingOrgRoster,
  useRecoverOnboardingStep,
  useRefreshOnboardingPreflight,
  useRefreshOnboardingRun,
  useSendOnboardingTestEmail,
  useSettleOnboardingStep,
} from '../../../api/hooks'
import {
  ONBOARDING_SETTLE_OUTCOMES,
  ONBOARDING_STEPS,
  type OnboardingPreflight,
  type OnboardingRun,
  type OnboardingStepName,
  type OnboardingTarget,
} from '../../../api/onboardingTypes'
import { isApiRecord } from '../../../api/responseDecoders'
import {
  batchTargets,
  blockedTargetIds,
  creditsConfirmation,
  creditWalletGroups,
  failedTargets,
  runBatch,
  stepOf,
  stepStatus,
  type BatchError,
  type BatchStop,
  type RowOutcome,
} from './bulkLogic'
import {
  claimedStepView,
  PRIMARY_BUTTON_CLASS,
  SECONDARY_BUTTON_CLASS,
  SMALL_PRIMARY_BUTTON_CLASS,
  STATUS_TONE,
} from './ui'
import EmailTemplateEditor from './EmailTemplateEditor'
import TeamOwnerSummary from './TeamOwnerSummary'

// Bulk run panel (plan B3–B7).
//
// STILL ONE STEP PER REQUEST. A batch is the operator choosing one step and
// confirming it for the rows that are ready; the console then dispatches that
// step for one target at a time, in row order. It is best-effort and resumable
// by the operator, not automatic: closing the tab stops dispatch. Everything
// that keeps that safe — ordering, single execution, recipient exclusivity, the
// credit ceiling — is enforced by the server inside each step claim.

function toBatchError(error: Error): BatchError {
  if (error instanceof ApiRequestError) {
    const detail = isApiRecord(error.errorData) ? error.errorData.detail : null
    if (isApiRecord(detail)) {
      return {
        status: error.status,
        code: typeof detail.code === 'string' ? detail.code : null,
        message: typeof detail.message === 'string' ? detail.message : error.message,
      }
    }
    return { status: error.status, code: null, message: error.message }
  }
  return { status: null, code: null, message: error.message }
}


interface BatchState {
  step: OnboardingStepName
  running: boolean
  current: string | null
  outcomes: Record<string, RowOutcome>
  stop: BatchStop | null
}

export default function BulkRunPanel({
  run: originalRun,
  preflight,
  targetIds,
}: {
  targetIds?: string[]
  run: OnboardingRun
  preflight: OnboardingPreflight | undefined
}) {
  const { t } = useTranslation('pages')
  const run = targetIds ? { ...originalRun, targets: originalRun.targets.filter((target) => targetIds.includes(target.target_id)), progress: undefined } : originalRun
  const scopeIds = targetIds ?? (originalRun.scope === 'targets' ? originalRun.scope_target_ids ?? originalRun.targets.map((target) => target.target_id) : undefined)
  const wholeRun = scopeIds === undefined && originalRun.scope !== 'targets'
  const advance = useAdvanceOnboardingTarget(scopeIds)
  const cancel = useCancelOnboardingRun()
  const settle = useSettleOnboardingStep(scopeIds)
  const recover = useRecoverOnboardingStep(scopeIds)
  const testSend = useSendOnboardingTestEmail(scopeIds)
  const refreshRun = useRefreshOnboardingRun(run.run_id, scopeIds)
  const refreshPreflight = useRefreshOnboardingPreflight(run.run_id, scopeIds)

  // The credits tick, keyed to the exact confirmation it was given for (scope +
  // rows + amounts). A tick for one row's retry must never count for a batch.
  const [creditsAck, setCreditsAck] = useState<string | null>(null)
  const [batch, setBatch] = useState<BatchState | null>(null)
  const [testSentTo, setTestSentTo] = useState<string | null>(null)
  const [orgMoveFor, setOrgMoveFor] = useState<string | null>(null)
  // A failed credits or email row is retried only through its own confirmation
  // (B5): the amount and balance, or the recipient and the attestation.
  const [retryConfirm, setRetryConfirm] = useState<{
    targetId: string
    step: OnboardingStepName
  } | null>(null)
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({})
  // A failed preflight re-read: what is on screen may be stale, so say so.
  const [preflightStale, setPreflightStale] = useState<string | null>(null)

  /** Re-read preflight; on failure keep the old report but flag it as stale. */
  const refreshPreflightOrFlag = async () => {
    try {
      await refreshPreflight()
      setPreflightStale(null)
    } catch (error) {
      setPreflightStale(error instanceof Error ? error.message : String(error))
    }
  }
  const stopRequested = useRef(false)
  const batchActive = useRef(false)

  const blocked = blockedTargetIds(preflight)
  const terminal = run.status === 'cancelled' || run.status === 'completed' || run.status === 'failed'
  const progress = run.progress

  const startBatch = async (step: OnboardingStepName, targetIds: string[]) => {
    if (batchActive.current || terminal || targetIds.length === 0) return
    batchActive.current = true
    setCreditsAck(null)
    stopRequested.current = false
    setBatch({ step, running: true, current: null, outcomes: {}, stop: null })
    try {
      // Fresh preflight at batch start: blocked rows are excluded from what the
      // server says NOW, not from whatever was on screen.
      const freshBlocked = blockedTargetIds(await refreshPreflight())
      setPreflightStale(null)
      const result = await runBatch(step, targetIds, freshBlocked, {
        advance: async (targetId) => {
          await advance.mutateAsync({ runId: run.run_id, targetId, step })
        },
        refetch: refreshRun,
        isStopped: () => stopRequested.current,
        toBatchError,
        teamNotReadyMessage: t('adminOnboarding.bulk.teamNotReady'),
        onRowStart: (targetId) =>
          setBatch((prev) => (prev ? { ...prev, current: targetId } : prev)),
        onRowDone: (targetId, outcome) =>
          setBatch((prev) =>
            prev
              ? { ...prev, current: null, outcomes: { ...prev.outcomes, [targetId]: outcome } }
              : prev,
          ),
      })
      // Steps change what preflight reports (account, balance, current org), and
      // the next confirmation is built from it: re-read once per batch, not per
      // step, and BEFORE the batch is marked idle so no confirmation can open
      // on the old report.
      await refreshPreflightOrFlag()
      setBatch((prev) =>
        prev ? { ...prev, running: false, current: null, outcomes: result.outcomes, stop: result.stop } : prev,
      )
    } catch (error) {
      // A refetch itself failed: stop and say so rather than guess.
      setBatch((prev) =>
        prev
          ? {
              ...prev,
              running: false,
              current: null,
              stop: { kind: 'halted', targetId: '', error: toBatchError(error instanceof Error ? error : new Error(String(error))) },
            }
          : prev,
      )
    } finally {
      batchActive.current = false
    }
  }

  const retryRow = (
    target: OnboardingTarget,
    step: OnboardingStepName,
    orgMove: { expectedCurrentOrg: string } | null = null,
  ) => {
    setRetryConfirm(null)
    setCreditsAck(null)
    setRowErrors((prev) => {
      const next = { ...prev }
      delete next[target.target_id]
      return next
    })
    void (async () => {
      try {
        await advance.mutateAsync({
          runId: run.run_id,
          targetId: target.target_id,
          step,
          // A move carries the source org the operator just reviewed; the
          // server refuses it if the account has moved since.
          options: orgMove
            ? { allowOrgMove: true, expectedCurrentOrg: orgMove.expectedCurrentOrg }
            : { allowOrgMove: false },
        })
      } catch (error) {
        // A recorded failure also shows on the row after the refetch, but a
        // claim refusal (409) or a server error changes nothing there — so
        // the response itself is kept and shown.
        const { message } = toBatchError(error instanceof Error ? error : new Error(String(error)))
        setRowErrors((prev) => ({ ...prev, [target.target_id]: message }))
      }
      setOrgMoveFor(null)
      try {
        await refreshRun()
      } catch (error) {
        const { message } = toBatchError(error instanceof Error ? error : new Error(String(error)))
        setRowErrors((prev) => ({ ...prev, [target.target_id]: message }))
      }
      // Separate from the row: a failed preflight read is not a failed retry.
      await refreshPreflightOrFlag()
    })()
  }

  /** Credits and email retry through a confirmation; other steps on the click. */
  const requestRetry = (target: OnboardingTarget, step: OnboardingStepName) => {
    if (step === 'credits' || step === 'email') {
      setCreditsAck(null)
      setRetryConfirm({ targetId: target.target_id, step })
    } else {
      retryRow(target, step)
    }
  }

  const batchRunning = batch?.running === true
  const ready = (step: OnboardingStepName) => batchTargets(run, step, blocked)
  const sharedOwners = run.targets.filter((owner) => owner.team_mode === 'create' &&
    run.targets.some((member) => member.team_mode === 'group_member' && member.team_owner_target_id === owner.target_id))

  return (
    <div className="mt-6 flex flex-col gap-4" data-testid="onboarding-bulk-run">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-sm font-semibold text-foreground">
          {t('adminOnboarding.bulk.runTitle', { count: run.targets.length })}
        </h2>
        {progress && (
          <span className="text-xs text-muted-foreground" data-testid="onboarding-bulk-progress">
            {t('adminOnboarding.bulk.progress', {
              completed: progress.completed_targets,
              attention: progress.attention_targets,
            })}{' '}
            · {t(`adminOnboarding.bulk.runStatus.${progress.status}`)}
          </span>
        )}
        {batchRunning && (
          <button
            type="button"
            onClick={() => {
              stopRequested.current = true
            }}
            className={`${SECONDARY_BUTTON_CLASS} ml-auto`}
            data-testid="onboarding-bulk-stop"
          >
            {t('adminOnboarding.bulk.stop')}
          </button>
        )}
        {!terminal && wholeRun && (
          <button
            type="button"
            onClick={() => {
              stopRequested.current = true
              cancel.mutate({ runId: run.run_id })
            }}
            className={`${SECONDARY_BUTTON_CLASS} ${batchRunning ? '' : 'ml-auto'}`}
            data-testid="onboarding-cancel-button"
          >
            {t('adminOnboarding.cancelRun')}
          </button>
        )}
      </div>
      {sharedOwners.map((owner) => <TeamOwnerSummary key={owner.target_id} email={owner.email ?? t('adminOnboarding.purgedRecipient')} />)}
      <p className="text-xs text-muted-foreground">{t('adminOnboarding.bulk.resumableHelp')}</p>
      {preflightStale && (
        <div
          className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning"
          data-testid="onboarding-bulk-preflight-stale"
        >
          {t('adminOnboarding.bulk.preflightStale', { message: preflightStale })}
        </div>
      )}
      {preflight && !preflight.trigger_enforcing && (
        <div
          className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
          data-testid="onboarding-blocking-reason"
        >
          {preflight.trigger_blocking_reason}
        </div>
      )}

      {/* Batch actions only; detailed progress stays in the per-recipient table. */}
      <ol className="flex flex-col gap-2" data-testid="onboarding-bulk-steps">
        {ONBOARDING_STEPS.map((step) => {
          const readyRows = ready(step)
          return (
            <li
              key={step}
              className="rounded-lg border border-border bg-surface-1 px-3 py-2.5"
              data-testid={`onboarding-bulk-step-${step}`}
            >
              <div className="flex flex-wrap items-center gap-3">
                <span className="flex-1 text-sm text-foreground">
                  {t(`adminOnboarding.step.${step}`)}
                </span>
                {readyRows.length > 0 && !terminal && step !== 'credits' && step !== 'email' && <button
                  type="button"
                  disabled={batchRunning}
                  onClick={() => {
                    setRetryConfirm(null)
                    void startBatch(step, readyRows.map((target) => target.target_id))
                  }}
                  className={SMALL_PRIMARY_BUTTON_CLASS}
                  data-testid={`onboarding-bulk-run-${step}`}
                >
                  {t('adminOnboarding.bulk.runFor', { count: readyRows.length })}
                </button>}
              </div>

              {/* P3: the run's email can be customised before the first send;
                  the server freezes it once any email has been attempted. */}
              {step === 'email' && !terminal && wholeRun && (
                <div className="mt-2">
                  <EmailTemplateEditor
                    runId={run.run_id}
                    previewTargetId={
                      readyRows[0]?.target_id ??
                      run.targets.find((target) => target.email !== null)?.target_id ??
                      null
                    }
                  />
                </div>
              )}

              {readyRows.length > 0 && !terminal && (step === 'credits' || step === 'email') && (
                <StepConfirmation
                  inline
                  busy={batchRunning}
                  step={step}
                  run={run}
                  readyRows={readyRows}
                  preflight={preflight}
                  ackScope={`batch:${step}`}
                  creditsAck={creditsAck}
                  onCreditsAck={setCreditsAck}
                  testSentTo={testSentTo}
                  testSendPending={testSend.isPending}
                  onTestSend={() => {
                    const first = readyRows[0]
                    if (!first) return
                    testSend.mutate(
                      { runId: run.run_id, targetId: first.target_id },
                      { onSuccess: (result) => setTestSentTo(result.sent_to) },
                    )
                  }}
                  onConfirm={() => {
                    setRetryConfirm(null)
                    void startBatch(step, readyRows.map((target) => target.target_id))
                  }}
                  onCancel={() => setCreditsAck(null)}
                />
              )}

              {failedTargets(run, step).length > 0 && !batchRunning && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('adminOnboarding.bulk.failedHint', { count: failedTargets(run, step).length })}
                </p>
              )}
            </li>
          )
        })}
      </ol>

      {retryConfirm &&
        (() => {
          const retryTarget = run.targets.find((t) => t.target_id === retryConfirm.targetId)
          if (!retryTarget) return null
          return (
            <div data-testid="onboarding-bulk-retry-confirm">
              <StepConfirmation
                step={retryConfirm.step}
                run={run}
                readyRows={[retryTarget]}
                preflight={preflight}
                ackScope={`retry:${retryTarget.target_id}:${retryConfirm.step}`}
                creditsAck={creditsAck}
                onCreditsAck={setCreditsAck}
                testSentTo={testSentTo}
                testSendPending={testSend.isPending}
                onTestSend={() =>
                  testSend.mutate(
                    { runId: run.run_id, targetId: retryTarget.target_id },
                    { onSuccess: (result) => setTestSentTo(result.sent_to) },
                  )
                }
                onConfirm={() => retryRow(retryTarget, retryConfirm.step)}
                onCancel={() => {
                  setRetryConfirm(null)
                  setCreditsAck(null)
                }}
              />
            </div>
          )
        })()}

      {batch && <BatchSummary batch={batch} run={run} />}

      {testSend.error instanceof Error && (
        <p className="text-xs text-destructive" data-testid="onboarding-bulk-test-send-error">
          {testSend.error.message}
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs" data-testid="onboarding-bulk-targets">
          <thead className="text-muted-foreground">
            <tr>
              <th className="py-1 pr-2">{t('adminOnboarding.bulk.colEmail')}</th>
              {ONBOARDING_STEPS.map((step) => (
                <th key={step} className="py-1 pr-2">
                  {t(`adminOnboarding.step.${step}`)}
                </th>
              ))}
              <th className="py-1 pr-2" />
            </tr>
          </thead>
          <tbody>
            {run.targets.map((target) => (
              <TargetRow
                key={target.target_id}
                run={run}
                target={target}
                blocked={blocked.has(target.target_id)}
                blockingReason={
                  preflight?.targets.find((p) => p.target_id === target.target_id)
                    ?.blocking_reason ?? null
                }
                inFlight={batch?.current === target.target_id}
                outcome={batch?.outcomes[target.target_id]}
                rowError={rowErrors[target.target_id] ?? null}
                currentOrg={(() => {
                  const report = preflight?.targets.find((p) => p.target_id === target.target_id)
                  return report ? { slug: report.current_org_slug } : null
                })()}
                actionsDisabled={batchRunning || advance.isPending || retryConfirm !== null}
                onRetry={(step) => requestRetry(target, step)}
                orgMoveOpen={orgMoveFor === target.target_id}
                onOrgMoveToggle={(open) => setOrgMoveFor(open ? target.target_id : null)}
                onOrgMoveRetry={(expectedCurrentOrg) =>
                  retryRow(target, 'org', { expectedCurrentOrg })
                }
                onSettle={(outcome) =>
                  settle.mutate({
                    runId: run.run_id,
                    targetId: target.target_id,
                    step: 'email',
                    outcome,
                  })
                }
                settlePending={settle.isPending}
                onRecover={(step) =>
                  recover.mutate({ runId: run.run_id, targetId: target.target_id, step })
                }
                recoverPending={recover.isPending}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function StepConfirmation({
  inline = false,
  busy = false,
  step,
  run,
  readyRows,
  preflight,
  ackScope,
  creditsAck,
  onCreditsAck,
  testSentTo,
  testSendPending,
  onTestSend,
  onConfirm,
  onCancel,
}: {
  inline?: boolean
  busy?: boolean
  step: OnboardingStepName
  run: OnboardingRun
  readyRows: OnboardingTarget[]
  preflight: OnboardingPreflight | undefined
  ackScope: string
  creditsAck: string | null
  onCreditsAck: (key: string | null) => void
  testSentTo: string | null
  testSendPending: boolean
  onTestSend: () => void
  onConfirm: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation('pages')
  const credits = step === 'credits' ? creditsConfirmation(readyRows, preflight) : null
  let wallets: ReturnType<typeof creditWalletGroups> = []
  let walletError = false
  if (credits) {
    try { wallets = creditWalletGroups(readyRows, preflight) }
    catch { walletError = true }
  }
  const hasTeamWallet = wallets.some((wallet) => wallet.kind === 'team')
  const onlyTeam = wallets.length === 1 && wallets[0].kind === 'team' ? wallets[0] : null
  const needsAttestation = step === 'email' && run.email_test_current !== true
  // The template is reviewed before a bulk send: no confirmation until the
  // server-rendered preview has actually loaded.
  const previewTargetId = step === 'email' ? (readyRows[0]?.target_id ?? null) : null
  const preview = useOnboardingEmailPreview(run.run_id, previewTargetId, previewTargetId !== null)
  // isFetching too: a cached preview from an earlier opening must not stand in
  // for the template as it is now.
  const needsPreview = step === 'email' && (!preview.isSuccess || preview.isFetching)
  const ackKey = credits
    ? `${ackScope}|${credits.rows.map((row) => `${row.target_id}:${row.credits}:${readyRows.find((target) => target.target_id === row.target_id)?.wallet_kind}:${readyRows.find((target) => target.target_id === row.target_id)?.credited_team_id}`).join(',')}|${wallets.map((wallet) => wallet.key).join(',')}`
    : null
  const creditsAcknowledged = ackKey !== null && creditsAck === ackKey
  const confirmDisabled =
    busy || walletError || readyRows.length === 0 ||
    (credits !== null && credits.count > 0 && !creditsAcknowledged) ||
    needsAttestation ||
    needsPreview

  return (
    <div
      className="mt-2 flex flex-col gap-2 rounded-lg border border-border bg-surface-2 p-3 text-xs"
      data-testid={`onboarding-bulk-confirm-${step}`}
    >
      {!inline && <p className="text-foreground">
        {t('adminOnboarding.bulk.confirmIntro', {
          step: t(`adminOnboarding.step.${step}`),
          count: readyRows.length,
        })}
      </p>}
      {step === 'allowlist' && (
        <p className="text-muted-foreground">{t('adminOnboarding.bulk.allowlistPerAddress')}</p>
      )}
      {step === 'org' && (
        <p className="text-muted-foreground">{t('adminOnboarding.bulk.orgNoMove')}</p>
      )}

      {walletError && <p role="alert" className="text-warning" data-testid="onboarding-wallet-error">{t('adminOnboarding.bulk.walletUnavailable')}</p>}
      {credits && !walletError && (
        <>
          <table className="w-full text-left" data-testid="onboarding-bulk-credits-table">
            <thead className="text-muted-foreground">
              <tr>
                <th className="pr-2">{t(hasTeamWallet ? 'adminOnboarding.bulk.colWallet' : 'adminOnboarding.bulk.colEmail')}</th>
                <th className="pr-2 text-right">{t('adminOnboarding.bulk.colCredits')}</th>
                <th className="pr-2 text-right">{t('adminOnboarding.bulk.colBalance')}</th>
              </tr>
            </thead>
            <tbody>
              {wallets.map((wallet) => (
                <tr key={wallet.key} data-testid="onboarding-credit-wallet">
                  <td className="pr-2 text-foreground">{wallet.kind === 'team' ? t('adminOnboarding.bulk.teamWallet', { name: wallet.name }) : wallet.name}</td>
                  <td className="pr-2 text-right text-foreground">{wallet.credits.toLocaleString()}</td>
                  <td className="pr-2 text-right text-muted-foreground">
                    {wallet.currentBalance === null ? '—' : wallet.currentBalance.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <label className="flex items-center gap-2 text-foreground">
            <input
              type="checkbox"
              checked={creditsAcknowledged}
              onChange={(e) => onCreditsAck(e.target.checked ? ackKey : null)}
              data-testid="onboarding-bulk-credits-confirm"
            />
            {t(onlyTeam ? 'adminOnboarding.bulk.creditsConfirmTeam' : hasTeamWallet ? 'adminOnboarding.bulk.creditsConfirmWallets' : 'adminOnboarding.bulk.creditsConfirm', {
              total: credits.total.toLocaleString(),
              name: onlyTeam?.name,
              count: hasTeamWallet ? wallets.length : credits.count,
            })}
          </label>
          {run.credit_ceiling !== undefined && (
            <p className="text-muted-foreground">
              {t('adminOnboarding.bulk.ceilingNote', {
                ceiling: run.credit_ceiling.toLocaleString(),
              })}
            </p>
          )}
        </>
      )}

      {step === 'email' && previewTargetId !== null && <EmailPreview preview={preview} />}

      {step === 'email' && (
        <>
          {/* Every recipient, with the parts of the template that vary: the
              greeting as it will render and the address (B5). */}
          <ul className="list-disc pl-5 text-foreground" data-testid="onboarding-bulk-email-recipients">
            {readyRows.map((target) => (
              <li key={target.target_id}>
                {t('adminOnboarding.bulk.greetingPreview', {
                  name: target.first_name ?? t('adminOnboarding.bulk.defaultGreeting'),
                })}{' '}
                — {target.email}
              </li>
            ))}
          </ul>
          {needsAttestation && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={testSendPending || readyRows.length === 0}
                onClick={onTestSend}
                className={SECONDARY_BUTTON_CLASS}
                data-testid="onboarding-bulk-test-send"
              >
                {t('adminOnboarding.emailTestSend')}
              </button>
              <span className="text-muted-foreground">
                {testSentTo
                  ? t('adminOnboarding.emailTestSent', { address: testSentTo })
                  : t('adminOnboarding.emailSendNeedsTest')}
              </span>
            </div>
          )}
        </>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={confirmDisabled}
          onClick={onConfirm}
          className={PRIMARY_BUTTON_CLASS}
          data-testid={inline ? `onboarding-bulk-run-${step}` : `onboarding-bulk-confirm-run-${step}`}
        >
          {t(inline ? 'adminOnboarding.bulk.runFor' : 'adminOnboarding.bulk.confirmRun', { count: readyRows.length })}
        </button>
        {!inline && <button type="button" onClick={onCancel} className={SECONDARY_BUTTON_CLASS}>
          {t('adminOnboarding.bulk.confirmCancel')}
        </button>}
      </div>
    </div>
  )
}

/**
 * The welcome email as the first recipient will get it — subject, body and any
 * transport warnings — so the template is reviewed before a bulk send, as on
 * the single-target ladder. The recipient list beside it shows what varies.
 */
function EmailPreview({ preview }: { preview: ReturnType<typeof useOnboardingEmailPreview> }) {
  const { t } = useTranslation('pages')
  return (
    <div data-testid="onboarding-bulk-email-preview">
      {preview.isLoading && (
        <p className="text-muted-foreground">{t('adminOnboarding.emailPreview.loading')}</p>
      )}
      {preview.error instanceof Error && (
        <p className="text-destructive" data-testid="onboarding-bulk-email-preview-error">
          {preview.error.message}
        </p>
      )}
      {preview.data && (
        <>
          {preview.data.warnings.map((warning) => (
            <p key={warning} className="text-warning" data-testid="onboarding-bulk-email-warning">
              {warning}
            </p>
          ))}
          <p className="text-foreground">
            <span className="text-muted-foreground">{t('adminOnboarding.emailPreview.subject')}</span>{' '}
            {preview.data.subject}
          </p>
          {/* sandbox="" — no scripts, no same-origin, no forms, no navigation. */}
          <iframe
            title={t('adminOnboarding.emailPreview.frameTitle')}
            sandbox=""
            referrerPolicy="no-referrer"
            srcDoc={preview.data.html}
            className="mt-1 h-80 w-full rounded border border-border bg-white"
            data-testid="onboarding-bulk-email-preview-frame"
          />
          {/* Some clients show only the text part, so it is reviewed too. */}
          <details className="mt-1 text-muted-foreground">
            <summary className="cursor-pointer">{t('adminOnboarding.emailPreview.plainText')}</summary>
            <pre
              className="mt-1 whitespace-pre-wrap rounded border border-border bg-surface-2 p-2 text-foreground"
              data-testid="onboarding-bulk-email-preview-text"
            >
              {preview.data.text}
            </pre>
          </details>
        </>
      )}
    </div>
  )
}

function BatchSummary({ batch, run }: { batch: BatchState; run: OnboardingRun }) {
  const { t } = useTranslation('pages')
  const stop = batch.stop
  const needsAttention = Object.values(batch.outcomes).some((outcome) => outcome.kind !== 'done' && outcome.kind !== 'done_by_others')
  if (!batch.running && stop?.kind === 'finished' && !needsAttention) return null
  const haltedEmail =
    stop?.kind === 'halted'
      ? run.targets.find((target) => target.target_id === stop.targetId)?.email
      : null
  return (
    <div
      className="rounded-lg border border-border bg-surface-1 p-3 text-xs"
      data-testid="onboarding-bulk-batch"
    >
      <p className="text-foreground">
        {t(`adminOnboarding.step.${batch.step}`)}
      </p>
      {batch.running && (
        <p className="text-muted-foreground">{t('adminOnboarding.bulk.batchRunning')}</p>
      )}
      {batch.stop?.kind === 'stopped' && (
        <p className="text-muted-foreground" data-testid="onboarding-bulk-stopped">
          {t('adminOnboarding.bulk.batchStopped')}
        </p>
      )}
      {batch.stop?.kind === 'cancelled' && (
        <p className="text-warning" data-testid="onboarding-bulk-cancelled">
          {t('adminOnboarding.bulk.batchCancelled')}
        </p>
      )}
      {batch.stop?.kind === 'halted' && (
        <p className="text-destructive" data-testid="onboarding-bulk-halted">
          {t('adminOnboarding.bulk.batchHalted', {
            email: haltedEmail ?? '—',
            message: batch.stop.error.message,
          })}
        </p>
      )}
    </div>
  )
}

function TargetRow({
  run,
  target,
  blocked,
  blockingReason,
  inFlight,
  outcome,
  rowError,
  currentOrg,
  actionsDisabled,
  onRetry,
  orgMoveOpen,
  onOrgMoveToggle,
  onOrgMoveRetry,
  onSettle,
  settlePending,
  onRecover,
  recoverPending,
}: {
  run: OnboardingRun
  target: OnboardingTarget
  blocked: boolean
  blockingReason: string | null
  inFlight: boolean
  outcome: RowOutcome | undefined
  rowError: string | null
  /** From this target's preflight; null until that report is available. */
  currentOrg: { slug: string | null } | null
  actionsDisabled: boolean
  onRetry: (step: OnboardingStepName) => void
  orgMoveOpen: boolean
  onOrgMoveToggle: (open: boolean) => void
  onOrgMoveRetry: (expectedCurrentOrg: string) => void
  onSettle: (outcome: (typeof ONBOARDING_SETTLE_OUTCOMES)[number]) => void
  settlePending: boolean
  onRecover: (step: OnboardingStepName) => void
  recoverPending: boolean
}) {
  const { t } = useTranslation('pages')
  const terminal = run.status === 'cancelled' || run.status === 'completed'
  const failedStep = ONBOARDING_STEPS.find((step) => stepStatus(target, step) === 'failed')
  const failed = failedStep ? stepOf(target, failedStep) : undefined
  const claimedStep = ONBOARDING_STEPS.find((step) => {
    const status = stepStatus(target, step)
    return status === 'claimed' || status === 'running'
  })
  const claimed = claimedStep ? stepOf(target, claimedStep) : undefined
  const claimedView = claimed ? claimedStepView(claimed.claimed_at, claimed.heartbeat_at) : null
  const orgMoveRequired = failedStep === 'org' && failed?.error_code === 'ORG_MOVE_REFUSED'
  const roster = useOnboardingOrgRoster(orgMoveOpen ? target.org_slug : null)

  return (
    <tr className="align-top" data-testid={`onboarding-bulk-target-${target.target_id}`}>
      <td className="py-1.5 pr-2 text-foreground">
        {target.email ?? t('adminOnboarding.purgedRecipient')}
        {target.team_mode === 'create' && <span className="ml-2 rounded border border-border bg-surface-2 px-1.5 py-0.5 font-medium text-foreground" data-testid={`onboarding-team-role-${target.target_id}`}>{t('adminOnboarding.direct.ownerLabel')}</span>}
        {target.team_mode === 'group_member' && <span className="ml-2 rounded border border-border bg-surface-2 px-1.5 py-0.5 text-muted-foreground" data-testid={`onboarding-team-role-${target.target_id}`}>{t('adminOnboarding.direct.memberLabel')}</span>}
        {target.team_mode === 'create' && <p className="text-xs text-muted-foreground">{t('adminOnboarding.team.summary.create', { name: target.team_name ?? '' })}</p>}
        {target.team_mode === 'group_member' && <p className="text-xs text-muted-foreground">{t('adminOnboarding.direct.memberSummary', { name: target.team_name ?? '' })}</p>}
        {inFlight && (
          <span className="ml-1 text-primary" data-testid="onboarding-bulk-in-flight">
            {t('adminOnboarding.bulk.inFlight')}
          </span>
        )}
        {blocked && blockingReason && (
          <p className="text-destructive">{blockingReason}</p>
        )}
        {outcome && outcome.kind !== 'done' && (
          <p className="text-muted-foreground" data-testid="onboarding-bulk-outcome">
            {t(`adminOnboarding.bulk.outcome.${outcome.kind}`)}
            {'message' in outcome ? `: ${outcome.message}` : ''}
          </p>
        )}
        {failed?.error_message && (
          <p className="text-destructive">
            {failed.error_code}: {failed.error_message}
          </p>
        )}
        {rowError && (
          <p className="text-destructive" data-testid={`onboarding-bulk-row-error-${target.target_id}`}>
            {rowError}
          </p>
        )}
      </td>
      {ONBOARDING_STEPS.map((step) => {
        const status = stepStatus(target, step)
        return (
          <td key={step} className="py-1.5 pr-2">
            <span
              className={`rounded border px-1 py-0.5 text-xs ${STATUS_TONE[status]}`}
              data-testid={`onboarding-bulk-status-${target.target_id}-${step}`}
            >
              {t(`adminOnboarding.status.${status}`)}
            </span>
          </td>
        )
      })}
      <td className="flex flex-col items-start gap-1 py-1.5 pr-2">
        {failedStep && !terminal && !orgMoveRequired && (
          <button
            type="button"
            disabled={actionsDisabled}
            onClick={() => onRetry(failedStep)}
            className={SECONDARY_BUTTON_CLASS}
            data-testid={`onboarding-bulk-retry-${target.target_id}`}
          >
            {t('adminOnboarding.bulk.retry', { step: t(`adminOnboarding.step.${failedStep}`) })}
          </button>
        )}
        {orgMoveRequired && !terminal && (
          <>
            <label className="flex items-center gap-1 text-muted-foreground">
              <input
                type="checkbox"
                checked={orgMoveOpen}
                onChange={(e) => onOrgMoveToggle(e.target.checked)}
                data-testid={`onboarding-bulk-org-move-${target.target_id}`}
              />
              {t('adminOnboarding.bulk.orgMoveOptIn')}
            </label>
            {orgMoveOpen && (
              <>
                {/* Old org -> new org, and who is already in the new one: the
                    move re-attributes this account's analytics. */}
                <p className="text-foreground" data-testid={`onboarding-bulk-org-move-detail-${target.target_id}`}>
                  {t('adminOnboarding.bulk.orgMoveFromTo', {
                    from: currentOrg?.slug ?? '—',
                    to: target.org_slug ?? '—',
                  })}
                </p>
                {!currentOrg?.slug && (
                  <p className="text-warning" data-testid={`onboarding-bulk-org-move-no-source-${target.target_id}`}>
                    {t('adminOnboarding.bulk.orgMoveNeedsPreflight')}
                  </p>
                )}
                <p className="text-muted-foreground">
                  {roster.isSuccess
                    ? t('adminOnboarding.bulk.orgRoster', {
                        slug: target.org_slug ?? '—',
                        members: roster.data.members.join(', ') || '—',
                      })
                    : t('adminOnboarding.bulk.orgRosterLoading')}
                </p>
                <button
                  type="button"
                  // Both sides of the move must be on screen: the source org
                  // (preflight) and who is already in the destination.
                  disabled={actionsDisabled || !roster.isSuccess || !currentOrg?.slug}
                  onClick={() => {
                    if (currentOrg?.slug) onOrgMoveRetry(currentOrg.slug)
                  }}
                  className={SECONDARY_BUTTON_CLASS}
                  data-testid={`onboarding-bulk-org-move-retry-${target.target_id}`}
                >
                  {t('adminOnboarding.bulk.orgMoveRetry')}
                </button>
              </>
            )}
          </>
        )}
        {stepStatus(target, 'email') === 'delivery_unknown' &&
          ONBOARDING_SETTLE_OUTCOMES.map((settleOutcome) => (
            <button
              key={settleOutcome}
              type="button"
              disabled={settlePending}
              onClick={() => onSettle(settleOutcome)}
              className={SECONDARY_BUTTON_CLASS}
              data-testid={`onboarding-bulk-settle-${settleOutcome}-${target.target_id}`}
            >
              {t(`adminOnboarding.settle.${settleOutcome}`)}
            </button>
          ))}
        {claimedStep && claimedView && (
          <>
            <span className={claimedView.interrupted ? 'text-warning' : 'text-muted-foreground'}>
              {claimedView.interrupted
                ? t('adminOnboarding.claimedInterrupted')
                : t('adminOnboarding.claimedRunning', { minutes: claimedView.claimedMinutes ?? 0 })}
            </span>
            {claimedView.canOfferRecover && (
              <button
                type="button"
                disabled={recoverPending}
                onClick={() => onRecover(claimedStep)}
                className={SECONDARY_BUTTON_CLASS}
                data-testid={`onboarding-bulk-recover-${target.target_id}`}
              >
                {t('adminOnboarding.recover')}
              </button>
            )}
          </>
        )}
      </td>
    </tr>
  )
}
